import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, Zap, Gauge, Share2, Instagram } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const imgUrl = (id, size) => `${API}/showcase/image/${id}${size === 'thumb' ? '?size=thumb' : ''}`;
export const workShareUrl = (work) => `${API}/share/work/${work.id}`;

const shareText = (work) => {
  const gain = work.hp_after && work.hp_before ? ` +${work.hp_after - work.hp_before} CV` : '';
  return `${work.brand} ${work.model}${work.year ? ` (${work.year})` : ''} — ${work.software}${gain} | AR Performance`;
};

const ShareButtons = ({ work, t }) => {
  const url = workShareUrl(work);
  const text = shareText(work);
  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`, '_blank', 'noopener');
  };
  const shareInstagram = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: text, text, url }); return; } catch { /* cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success(t('Enlace copiado — pégalo en tu historia o publicación de Instagram'));
    } catch {
      toast.error(url);
    }
  };
  return (
    <div className="flex items-center gap-2" data-testid="share-buttons">
      <button onClick={shareWhatsApp} title="WhatsApp"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-green-600/15 text-green-400 border border-green-600/30 hover:bg-green-600/25 transition-colors text-xs font-medium"
        data-testid={`share-whatsapp-${work.id}`}>
        <Share2 className="w-3.5 h-3.5" /> WhatsApp
      </button>
      <button onClick={shareInstagram} title="Instagram"
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-pink-600/15 text-pink-400 border border-pink-600/30 hover:bg-pink-600/25 transition-colors text-xs font-medium"
        data-testid={`share-instagram-${work.id}`}>
        <Instagram className="w-3.5 h-3.5" /> Instagram
      </button>
    </div>
  );
};

export const WorkCard = ({ work, onClick }) => {
  const { t } = useTranslation();
  const gain = work.hp_after && work.hp_before ? work.hp_after - work.hp_before : null;
  return (
    <div onClick={onClick} className="bg-surface border border-white/10 rounded-md overflow-hidden cursor-pointer hover:border-primary/40 transition-all group"
      data-testid={`work-card-${work.id}`}>
      <div className="relative h-48 overflow-hidden bg-black">
        {work.photo_ids?.[0] && (
          <img src={imgUrl(work.photo_ids[0], 'thumb')} alt={`${work.brand} ${work.model}`} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        )}
        {gain !== null && gain > 0 && (
          <span className="absolute top-2 right-2 bg-primary text-white text-xs font-heading font-bold px-2 py-1 rounded">+{gain} CV</span>
        )}
      </div>
      <div className="p-4">
        <h3 className="font-heading text-white font-bold uppercase truncate">{work.brand} {work.model} {work.year && `(${work.year})`}</h3>
        {work.engine && <p className="text-zinc-400 text-xs mt-0.5">{work.engine}</p>}
        <p className="text-primary text-sm font-medium mt-0.5">{work.software}</p>
        {(work.ecu_type || work.tool) && (
          <p className="text-zinc-500 text-xs mt-0.5 font-mono truncate">{[work.ecu_type, work.tool].filter(Boolean).join(' · ')}</p>
        )}
        {work.hp_before && work.hp_after && (
          <p className="text-zinc-400 text-xs mt-1 font-mono">{work.hp_before} CV → <span className="text-green-400 font-bold">{work.hp_after} CV</span>
            {work.nm_before && work.nm_after && <> · {work.nm_before} Nm → <span className="text-green-400 font-bold">{work.nm_after} Nm</span></>}
          </p>
        )}
      </div>
    </div>
  );
};

export const WorkModal = ({ work, onClose }) => {
  const { t } = useTranslation();
  const [activePhoto, setActivePhoto] = useState(0);
  useEffect(() => { setActivePhoto(0); }, [work]);
  useEffect(() => {
    if (!work) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
  }, [work, onClose]);
  if (!work) return null;
  return createPortal((
    <div className="fixed inset-0 z-[9999] bg-black/80 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="work-modal" role="dialog" aria-modal="true">
      <div className="bg-surface border border-white/10 rounded-md max-w-3xl w-full my-2 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 p-4 border-b border-white/10 shrink-0">
          <div className="min-w-0">
            <h3 className="font-heading text-white font-bold text-base sm:text-lg uppercase break-words">{work.brand} {work.model} {work.year && `(${work.year})`}</h3>
            {work.engine && <p className="text-zinc-400 text-xs">{work.engine}</p>}
            <p className="text-primary text-sm break-words">{work.software}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white shrink-0" data-testid="work-modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-4 overflow-y-auto">
          <div className="relative rounded-md overflow-hidden bg-black">
            <img src={imgUrl(work.photo_ids[activePhoto])} alt="" className="w-full max-h-[45vh] sm:max-h-[380px] object-contain" />
          </div>
          {work.photo_ids.length > 1 && (
            <div className="flex gap-2 overflow-x-auto">
              {work.photo_ids.map((pid, i) => (
                <img key={pid} src={imgUrl(pid, 'thumb')} alt="" onClick={() => setActivePhoto(i)}
                  className={`w-16 h-16 object-cover rounded cursor-pointer border-2 ${i === activePhoto ? 'border-primary' : 'border-transparent opacity-60'}`} />
              ))}
            </div>
          )}
          {(work.hp_before || work.nm_before) && (
            <div className="grid grid-cols-2 gap-3">
              {work.hp_before && work.hp_after && (
                <div className="bg-surface-highlight border border-white/5 rounded-md p-3 flex items-center gap-3">
                  <Zap className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-zinc-500 text-xs uppercase">{t('Potencia')}</p>
                    <p className="text-white font-mono">{work.hp_before} → <span className="text-green-400 font-bold">{work.hp_after} CV</span></p>
                  </div>
                </div>
              )}
              {work.nm_before && work.nm_after && (
                <div className="bg-surface-highlight border border-white/5 rounded-md p-3 flex items-center gap-3">
                  <Gauge className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-zinc-500 text-xs uppercase">{t('Par motor')}</p>
                    <p className="text-white font-mono">{work.nm_before} → <span className="text-green-400 font-bold">{work.nm_after} Nm</span></p>
                  </div>
                </div>
              )}
            </div>
          )}
          {(work.ecu_type || work.sw || work.hw || work.tool) && (
            <div className="bg-surface-highlight border border-white/5 rounded-md p-3">
              <p className="text-zinc-500 text-xs uppercase tracking-wider mb-2">{t('Ficha técnica')}</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                {work.ecu_type && <div className="flex justify-between gap-2"><span className="text-zinc-500">{t('Centralita')}</span><span className="text-white font-mono truncate">{work.ecu_type}</span></div>}
                {work.tool && <div className="flex justify-between gap-2"><span className="text-zinc-500">{t('Máquina')}</span><span className="text-white font-mono truncate">{work.tool}</span></div>}
                {work.sw && <div className="flex justify-between gap-2"><span className="text-zinc-500">SW</span><span className="text-white font-mono truncate">{work.sw}</span></div>}
                {work.hw && <div className="flex justify-between gap-2"><span className="text-zinc-500">HW</span><span className="text-white font-mono truncate">{work.hw}</span></div>}
              </div>
            </div>
          )}
          {work.chart_id && (
            <div>
              <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2">{t('Gráfica de potencia')}</p>
              <img src={imgUrl(work.chart_id)} alt="dyno" className="w-full rounded-md border border-white/10 bg-white" data-testid="work-chart" />
            </div>
          )}
          {work.description && <p className="text-zinc-400 text-sm whitespace-pre-line">{work.description}</p>}
          <div className="flex items-center justify-between pt-2 border-t border-white/5">
            <p className="text-zinc-500 text-xs uppercase tracking-wider">{t('Compartir')}</p>
            <ShareButtons work={work} t={t} />
          </div>
        </div>
      </div>
    </div>
  ), document.body);
};
